import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// ************ CONSTANTS *************
import {
  ENABLE_ACCOMMODATIONS,
  ENABLE_EXTRAS,
  EXCLUDE_KENNELS_FROM_ACCOMMODATIONS,
  FLOW_CLIENT_ID,
  ModifyFlowType
} from '../../configuration/constants'
import appRoutes from '../../configuration/appRoutes'

// ************ ACTIONS *************
import {
  setActiveAccommodation,
  setActiveDepartureAccommodations,
  setActiveReturnAccommodations
} from '../../redux/activeAccommodations/actions'
import { fetchCrossing } from '../../redux/crossings/actions'
import {
  updateDepartureAccommodations,
  updateReturnAccommodations
} from '../../redux/userSelections/actions'

// *********** COMPONENTS ***********
import BrandLoader from '../../components/BrandLoader/BrandLoader'
import AccommodationCard from '../../components/accommodations/AccommodationCard'
import OpenableAccommodationCard from '../../components/accommodations/OpenableAccommodationCard'
import PageWarningBanner from '../../components/PageWarningBanner/PageWarningBanner'

// ********* SELECTORS **************
import {
  selectAvailableAccommodations,
  selectAvailableAccommodationsExcludingKennels
} from '../../redux/activeCrossings/selectors'
import {
  selectAccommodationsContent,
  selectLabels
} from '../../redux/configuration/selectors'
import { selectActiveAccommodations } from '../../redux/activeAccommodations/selectors'
import { selectCrossingData } from '../../redux/crossings/selectors'
import { selectActiveRouteForm } from '../../redux/ferryRouteSelections/selectors'
import {
  selectActiveModifyBooking,
  selectModifyFlowType
} from '../../redux/modifyBooking/selectors'

// *********** UTILITIES ************
import { sortObjectByKey } from '../../configuration/utilities'
// *********** STYLES ***************
import '../../styles/scss/components/accommodations/accommodations.scss'

const AccommodationsContainer = ({ handleRouteChange }) => {
  const dispatch = useDispatch()
  const activeRouteForm = useSelector(selectActiveRouteForm)
  const modifyFlowType = useSelector(selectModifyFlowType)
  const activeModifyBooking = useSelector(selectActiveModifyBooking)
  useEffect(() => {
    dispatch(
      fetchCrossing('departure', { fetchAllAccommodationResources: true })
    )
    if (activeRouteForm === 'RT') {
      dispatch(
        fetchCrossing('return', { fetchAllAccommodationResources: true })
      )
    }

    if (activeModifyBooking) {
      if (
        activeModifyBooking?.departureAccommodations &&
        ENABLE_ACCOMMODATIONS
      ) {
        dispatch(
          updateDepartureAccommodations(
            activeModifyBooking?.departureAccommodations
          )
        )
        // TODO: deprecate
        dispatch(
          setActiveDepartureAccommodations(
            activeModifyBooking?.departureAccommodations
          )
        )
      }

      if (activeModifyBooking?.returnAccommodations && ENABLE_ACCOMMODATIONS) {
        dispatch(
          updateReturnAccommodations(activeModifyBooking?.returnAccommodations)
        )
        // TODO: deprecate
        dispatch(
          setActiveReturnAccommodations(
            activeModifyBooking?.returnAccommodations
          )
        )
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const crossingState = useSelector(selectCrossingData)

  const isLoading =
    crossingState?.departure?.loading || crossingState?.return?.loading

  const labels = useSelector(selectLabels)
  const activeAccommodations = useSelector(selectActiveAccommodations)
  const accommodationsContent = useSelector(selectAccommodationsContent)

  let availableAccommodations
  const availableAccommodationsAll = useSelector(selectAvailableAccommodations)
  const availableAccommodationsNoKennels = useSelector(
    selectAvailableAccommodationsExcludingKennels
  )

  if (EXCLUDE_KENNELS_FROM_ACCOMMODATIONS) {
    availableAccommodations = availableAccommodationsNoKennels
  } else {
    availableAccommodations = availableAccommodationsAll
  }

  function getDirectionValue(accommodation, direction) {
    return (
      activeAccommodations?.[direction]?.[accommodation.details.resourceCode]
        ?.count || 0
    )
  }

  function handleCountChange(accommodation, direction, addend) {
    if (ENABLE_ACCOMMODATIONS) {
      if (direction === 'departure') {
        const departureAccommodations = {}
        departureAccommodations[accommodation.details.resourceCode] =
          getDirectionValue(accommodation, direction) + addend
        dispatch(updateDepartureAccommodations(departureAccommodations))
      } else {
        const returnAccommodations = {}
        returnAccommodations[accommodation.details.resourceCode] =
          getDirectionValue(accommodation, direction) + addend
        dispatch(updateReturnAccommodations(returnAccommodations))
      }
      // TODO: deprecate
      dispatch(
        setActiveAccommodation({
          code: accommodation.details.resourceCode,
          count: getDirectionValue(accommodation, direction) + addend,
          direction,
          accommodation: accommodation[direction]
        })
      )
    }
  }

  let continueBtnLabel, continueBtnRoute
  if (ENABLE_EXTRAS) {
    continueBtnRoute = appRoutes.extras.pathname
    continueBtnLabel = labels.continueToExtrasBtn
  } else {
    continueBtnRoute = appRoutes.summary.pathname
    continueBtnLabel = labels.continueToSummaryBtn
  }

  let cardLayoutClass, CardComponent
  switch (FLOW_CLIENT_ID) {
    case 'mai':
      cardLayoutClass = 'accommodations-list-horizontal'
      CardComponent = OpenableAccommodationCard
      break
    default:
      cardLayoutClass = 'accommodations-list'
      CardComponent = AccommodationCard
      break
  }

  return (
    <div
      className={`accommodations u-container u-page-view-container ${FLOW_CLIENT_ID}`}
    >
      <h1 className="page-title theme-font-header">{labels.services}</h1>
      {accommodationsContent?.warnings?.length ? (
        <div className="accommodations-warnings">
          {accommodationsContent.warnings.map(warning => {
            return (
              <PageWarningBanner
                message={warning.message}
                key={warning.message}
              >
                <span
                  className="accommodations-warning-message"
                  dangerouslySetInnerHTML={{ __html: warning.message }}
                />
              </PageWarningBanner>
            )
          })}
        </div>
      ) : null}
      {labels.covidCabinMsg && (
        <div className="accommodations-warnings">
          <PageWarningBanner
            message={labels.covidCabinMsg}
            key={labels.covidCabinMsg}
          >
            <span
              className="accommodations-warning-message"
              dangerouslySetInnerHTML={{ __html: labels.covidCabinMsg }}
            />
          </PageWarningBanner>
        </div>
      )}
      <div className="content">
        {isLoading ? (
          <div style={{ flex: 1 }}>
            <BrandLoader />
          </div>
        ) : (
          <>
            {availableAccommodations &&
            Object.keys(availableAccommodations).length ? (
              <div className={cardLayoutClass}>
                {sortObjectByKey(availableAccommodations).map(
                  ([key, accommodation]) => {
                    return (
                      <CardComponent
                        key={key}
                        accommodation={accommodation}
                        content={
                          accommodationsContent &&
                          accommodationsContent.accommodations
                            ? accommodationsContent.accommodations[key]
                            : {}
                        }
                        handleCountChange={handleCountChange}
                        curDepartureCount={getDirectionValue(
                          accommodation,
                          'departure'
                        )}
                        curReturnCount={getDirectionValue(
                          accommodation,
                          'return'
                        )}
                        isOneWay={activeRouteForm === 'OW'}
                        editReturnOnly={
                          modifyFlowType === ModifyFlowType.DEPARTURE_LOCKED
                        }
                      />
                    )
                  }
                )}
              </div>
            ) : (
              <p className="u-flex-grow u-text-center">
                {labels.noAvailableAccommodations}
              </p>
            )}
          </>
        )}

        {accommodationsContent?.sidebars?.length ? (
          <div className={'additional-information'}>
            {accommodationsContent.sidebars.map(sidebar => {
              const { caption, textBlocks } = sidebar

              const image = sidebar.images?.[0]

              return (
                <div className="additional-information-card" key={caption}>
                  <div className="image-container">
                    {image && <img src={image.path} alt="" />}
                  </div>
                  <div className="body">
                    <p className="title theme-title-variant-four">{caption}</p>

                    {textBlocks && textBlocks.length
                      ? textBlocks.map(block => (
                          <p className="copy" key={block}>
                            {block}
                          </p>
                        ))
                      : null}
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
      <div className="footer">
        {accommodationsContent?.messagesBottom?.length ? (
          <div>
            {accommodationsContent.messagesBottom.map(message => {
              return (
                <div
                  className={`copy ${message.type}`}
                  key={message.message}
                  dangerouslySetInnerHTML={{ __html: message.message }}
                />
              )
            })}
          </div>
        ) : null}
      </div>
      <div className="u-page-submit-container">
        <button
          className="btn btn-primary large-primary-btn"
          onClick={() => handleRouteChange(continueBtnRoute)}
        >
          {continueBtnLabel} →
        </button>
      </div>
    </div>
  )
}

export default AccommodationsContainer
